/**
 * The registry twin copy (NO-651), one copy for both sides: the Add RD dialog
 * shows it before submit, and POST /api/admin/rds returns it on the 409
 * `rd_name_conflict` race backstop. Human-approved: no trailing period.
 */
export function registryTwinMessage(existingRdName: string): string {
  return `An RD named ${existingRdName} is already in the registry. Please inform Justin via Chat to fix`;
}

/**
 * The RD Tombstone copy (NO-695), one copy for both sides: the server answers
 * its refusals with it and the /admin/rds Tombstone dialog shows it. No
 * trailing period, per the approved precedent above.
 */
export const RD_TOMBSTONE_ACTION = "Tombstone";

export const RD_TOMBSTONED_SECTION_TITLE = "Tombstoned";

/** Every refused write to a tombstoned RD's records (409 `rd_tombstoned`). */
export const RD_TOMBSTONED_MESSAGE =
  "This RD is tombstoned. Its history can be read, but nothing can be added or changed";

/** 409 `rd_sign_in_linked`. The dialog links "Provider Links in NDD Admin". */
export const RD_TOMBSTONE_SIGN_IN_LINKED_MESSAGE =
  "This RD has a linked sign-in account. Unlink it on Provider Links in NDD Admin first";

/** 409 `rd_healthie_open`. */
export const RD_TOMBSTONE_HEALTHIE_OPEN_MESSAGE =
  "This RD is still live in Healthie. Archive them in Healthie first";

/** 400 `rd_tombstone_name_mismatch`; the dialog keeps Confirm disabled first. */
export const RD_TOMBSTONE_NAME_MISMATCH_MESSAGE =
  "Type the RD's name exactly as it is shown to confirm";

/** 400 `rd_tombstone_reason_required`; the dialog keeps Confirm disabled first. */
export const RD_TOMBSTONE_REASON_REQUIRED_MESSAGE =
  "Add a reason for tombstoning this RD";

/** AC2: the typed name must equal the displayed name exactly (no trim, no case folding). */
export function tombstoneNameMatches(typedName: string, rdName: string): boolean {
  return typedName === rdName;
}

/** A reason counts once it holds a non-space character, as the column's CHECK requires. */
export function tombstoneReasonIsValid(reason: string): boolean {
  return reason.trim().length > 0;
}

// ---------------------------------------------------------------------------
// RD removal (NO-695 QA repair, spec "RD Removal: Final Design" section 4).
// Every live row has one Remove control; the server's `removal` picks the
// dialog. Dialog body sentences keep their periods; labels and refusals none.
// ---------------------------------------------------------------------------

/**
 * The singular and plural label of each counted history kind, keyed by its
 * `RdDeleteBlockers` field in `COUNTED_RD_HISTORY` order. The server's blocked
 * message and the dialog's "History kept" line both read these.
 */
export const RD_HISTORY_LABELS = {
  sessionCount: { singular: "session", plural: "sessions" },
  chatCount: { singular: "chat thread", plural: "chat threads" },
  observationCount: { singular: "observation", plural: "observations" },
  clinicalSessionCount: { singular: "clinical session", plural: "clinical sessions" },
  formProfileCount: { singular: "form profile", plural: "form profiles" },
  formSnapshotCount: { singular: "form snapshot", plural: "form snapshots" },
  meetingNoteCount: { singular: "meeting note", plural: "meeting notes" },
  meetingNoteShareCount: {
    singular: "meeting-note share",
    plural: "meeting-note shares",
  },
} as const;

export type RdHistoryLabelKey = keyof typeof RD_HISTORY_LABELS;

/** "History kept: 30 sessions, 2 chat threads"; null when every count is 0. */
export function rdHistoryKeptLine(
  blockers: Partial<Record<RdHistoryLabelKey, number>>
): string | null {
  const parts = (Object.keys(RD_HISTORY_LABELS) as RdHistoryLabelKey[])
    .filter((key) => (blockers[key] ?? 0) > 0)
    .map((key) => historyPart(key, blockers[key] ?? 0));

  return parts.length === 0 ? null : `History kept: ${parts.join(", ")}`;
}

function historyPart(key: RdHistoryLabelKey, count: number): string {
  const label = RD_HISTORY_LABELS[key];

  return `${count} ${count === 1 ? label.singular : label.plural}`;
}

export const RD_REMOVE_ACTION = "Remove RD";

export const RD_STRANDED_ROW_HINT =
  "link them in NDD Admin, or remove them if they've left";

export const RD_PROVIDER_LINKS_LABEL = "Provider Links in NDD Admin";

export const RD_CLOSE_ACTION = "Close";

export function rdDeleteTitle(name: string): string {
  return `Delete ${name}?`;
}

export function rdDeleteBody(name: string): string {
  return `${name} has no sessions or other history, so the record can be deleted outright. They disappear from every list and picker. This cannot be undone.`;
}

export function rdTombstoneTitle(name: string): string {
  return `Tombstone ${name}?`;
}

export function rdTombstoneBody(name: string): string {
  return `${name} has history, so the record is kept and marked Tombstoned. They disappear from every list and picker, nothing new can be added to their records, and what exists stays readable. They move to Tombstoned at the bottom of this page. There is no restore.`;
}

export function rdTombstoneNameLabel(name: string): string {
  return `Type ${name} to confirm`;
}

export const RD_TOMBSTONE_REASON_LABEL = "Reason";

export const RD_TOMBSTONE_REASON_PLACEHOLDER =
  'Why they\'re being removed, for example "Left the practice in September"';

export const RD_TOMBSTONE_PENDING = "Tombstoning…";

export function rdLinkedTitle(name: string): string {
  return `${name} is still linked`;
}

export function rdLinkedBody(name: string): string {
  return `A linked RD can't be removed. ${name} has history, so removing them means tombstoning the record, and that needs every link gone first:`;
}

/** A note whose middle part is a link: lead, link label, tail. */
export interface RdLinkedNoteParts {
  lead: string;
  link: string;
  tail: string;
}

/** Shown when the row shows the account. The link is Provider Links in NDD Admin. */
export function rdLinkedSignInNote(email: string): RdLinkedNoteParts {
  return {
    lead: `Sign-in account ${email}. Unlink it on `,
    link: RD_PROVIDER_LINKS_LABEL,
    tail: ".",
  };
}

/** Shown when signInLinked is true but the row shows no account. */
export const RD_LINKED_SIGN_IN_HIDDEN_NOTE: RdLinkedNoteParts = {
  lead: "Sign-in account still attached. Their Provider record still holds a sign-in user, though this page shows none. Unlink it on ",
  link: RD_PROVIDER_LINKS_LABEL,
  tail: ".",
};

/** "their Healthie record" links the row's own healthieRecordUrl. */
export const RD_LINKED_HEALTHIE_NOTE: RdLinkedNoteParts = {
  lead: "Live in Healthie. Archive ",
  link: "their Healthie record",
  tail: ".",
};

/** Shown when healthieOpen is true but the row shows no record. No link. */
export const RD_LINKED_HEALTHIE_HIDDEN_NOTE =
  "Healthie still lists them as live. This page shows no record. Archive them in Healthie.";

export const RD_LINKED_CLOSE_LINE =
  "Once the links are gone, Remove will tombstone them.";

export function rdTombstonedToast(name: string): string {
  return `Tombstoned ${name}`;
}

export const RD_TOMBSTONED_TOAST_DETAIL =
  "They're under Tombstoned at the bottom of this page.";
